import React, { FC, useEffect, useRef } from 'react';
import classes from './Confetti.module.css';

const DURATION = 3000;
const VOLLEY_MS = 120;
const START_VELOCITY = 30;
const TICKS = 45;         // frames a piece lives for
const DECAY = 0.9;        // velocity retained each frame
const GRAVITY = 3;
const COLORS = [
  [ 38, 204, 255 ], [ 162, 90, 253 ], [ 255, 94, 126 ], [ 136, 255, 90 ],
  [ 252, 255, 66 ], [ 255, 166, 45 ], [ 255, 54, 255 ],
];

interface Fetti {
  x: number;
  y: number;
  velocity: number;
  angle: number;
  colour: number[];
  wobble: number;
  wobbleSpeed: number;
  tilt: number;
  jitter: number;
  tick: number;
}

const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

const Confetti: FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const pieces: Fetti[] = [];

    // spread: 360 — the burst throws in every direction from its origin.
    const burst = (originX: number, originY: number, count: number) => {
      for (let index = 0; index < count; index += 1) {
        pieces.push({
          x:           originX * width,
          y:           originY * height,
          velocity:    START_VELOCITY * 0.5 + Math.random() * START_VELOCITY,
          angle:       Math.random() * Math.PI * 2,
          colour:      COLORS[Math.floor(Math.random() * COLORS.length)],
          wobble:      Math.random() * 10,
          wobbleSpeed: Math.min(0.11, Math.random() * 0.1 + 0.05),
          tilt:        randomInRange(0.25, 0.75) * Math.PI,
          jitter:      Math.random() + 2,
          tick:        0,
        });
      }
    };

    const draw = (piece: Fetti) => {
      // A quadrilateral whose corners oscillate, so the piece reads as paper
      // turning over rather than a flat rectangle.
      const wobbleX = piece.x + 10 * Math.cos(piece.wobble);
      const wobbleY = piece.y + 10 * Math.sin(piece.wobble);
      const tiltCos = Math.cos(piece.tilt) * piece.jitter;
      const tiltSin = Math.sin(piece.tilt) * piece.jitter;
      const [ r, g, b ] = piece.colour;

      context.fillStyle = `rgba(${r}, ${g}, ${b}, ${1 - piece.tick / TICKS})`;
      context.beginPath();
      context.moveTo(Math.floor(piece.x), Math.floor(piece.y));
      context.lineTo(Math.floor(wobbleX), Math.floor(piece.y + tiltSin));
      context.lineTo(Math.floor(wobbleX + tiltCos), Math.floor(wobbleY + tiltSin));
      context.lineTo(Math.floor(piece.x + tiltCos), Math.floor(wobbleY));
      context.closePath();
      context.fill();
    };

    const animationEnd = Date.now() + DURATION;

    const volley = window.setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        window.clearInterval(volley);
        return;
      }

      // Thins out as the celebration winds down.
      const count = Math.round(50 * (timeLeft / DURATION));
      // Particles fall, so start a little above a random point.
      burst(randomInRange(0.1, 0.3), Math.random() - 0.2, count);
      burst(randomInRange(0.7, 0.9), Math.random() - 0.2, count);
    }, VOLLEY_MS);

    let frameId = 0;

    const frame = () => {
      context.clearRect(0, 0, width, height);

      for (let index = pieces.length - 1; index >= 0; index -= 1) {
        const piece = pieces[index];

        piece.x += Math.cos(piece.angle) * piece.velocity;
        piece.y += Math.sin(piece.angle) * piece.velocity + GRAVITY;
        piece.velocity *= DECAY;
        piece.wobble += piece.wobbleSpeed;
        piece.tilt += 0.1;
        piece.jitter = Math.random() + 2;
        piece.tick += 1;

        if (piece.tick >= TICKS) {
          pieces.splice(index, 1);
          continue;
        }
        draw(piece);
      }

      frameId = requestAnimationFrame(frame);
    };
    frameId = requestAnimationFrame(frame);

    return () => {
      window.clearInterval(volley);
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className={classes.canvas} aria-hidden={true}/>;
};

export default Confetti;
